/**
 * Signed remote-control requests. When the platform reaches a workbench over
 * the mesh (through its gateway), there is no relay stream to vouch for the
 * caller, so every request carries X-Aioe-Signature: a compact JWS (EdDSA,
 * Ed25519) the platform signs and the workbench verifies against the keys
 * published at /.well-known/aioe-jwks.json. The claims bind the request to
 * one device, one body and a short lifetime.
 *
 * WebCrypto only, so the same code runs in Node (the API) and in the
 * workbench's host or desktop process.
 *
 * Peer tokens (`typ: aioe-peer+jws`, ./assignments.ts) use the same key: a
 * desktop presents one to a node, which verifies it with `verifyPeerToken`.
 * Both verifiers take an optional `seen` guard that refuses a `jti` twice.
 */
export const SIGNATURE_HEADER = "X-Aioe-Signature";
export const JWKS_PATH = "/.well-known/aioe-jwks.json";
const enc = new TextEncoder();
function b64url(bytes) {
    const u = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
    let s = "";
    for (const b of u)
        s += String.fromCharCode(b);
    return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
function fromB64url(s) {
    const bin = atob(s.replace(/-/g, "+").replace(/_/g, "/") + "===".slice((s.length + 3) % 4));
    return Uint8Array.from(bin, (c) => c.charCodeAt(0));
}
export async function sha256B64url(text) {
    return b64url(await crypto.subtle.digest("SHA-256", enc.encode(text)));
}
export const CONTROL_TYP = "aioe-control+jws";
export const PEER_TYP = "aioe-peer+jws";
/** A compact EdDSA JWS over any claims. */
export async function signJws(privateKey, kid, typ, claims) {
    const head = b64url(enc.encode(JSON.stringify({ alg: "EdDSA", typ, kid })));
    const body = b64url(enc.encode(JSON.stringify(claims)));
    const sig = await crypto.subtle.sign({ name: "Ed25519" }, privateKey, enc.encode(`${head}.${body}`));
    return `${head}.${body}.${b64url(sig)}`;
}
export async function signControlRequest(privateKey, kid, claims) {
    return signJws(privateKey, kid, CONTROL_TYP, claims);
}
export class SignatureError extends Error {
}
export function createJtiGuard(maxEntries = 100_000) {
    const seen = new Map();
    return {
        accept(jti, exp, now) {
            if (seen.size > maxEntries / 2)
                for (const [k, e] of seen)
                    if (e < now)
                        seen.delete(k);
            if (seen.has(jti))
                return false;
            if (seen.size >= maxEntries)
                return false; // full of live tokens: refuse rather than forget one
            seen.set(jti, exp);
            return true;
        },
    };
}
export async function verifyJws(token, jwks) {
    const parts = token.split(".");
    if (parts.length !== 3)
        throw new SignatureError("malformed signature");
    const [h, p, s] = parts;
    let header;
    try {
        header = JSON.parse(new TextDecoder().decode(fromB64url(h)));
    }
    catch {
        throw new SignatureError("malformed signature");
    }
    if (header.alg !== "EdDSA")
        throw new SignatureError("unexpected algorithm");
    const jwk = jwks.keys.find((k) => k.kid === header.kid);
    if (!jwk)
        throw new SignatureError("unknown signing key");
    const key = await crypto.subtle.importKey("jwk", { kty: jwk.kty, crv: jwk.crv, x: jwk.x }, { name: "Ed25519" }, false, ["verify"]);
    const ok = await crypto.subtle.verify({ name: "Ed25519" }, key, fromB64url(s), enc.encode(`${h}.${p}`));
    if (!ok)
        throw new SignatureError("bad signature");
    return { typ: header.typ, claims: JSON.parse(new TextDecoder().decode(fromB64url(p))) };
}
/**
 * Verify a peer token for this node: signed by the platform, for this node, not expired, living
 * at most `maxLifetimeSeconds` (600), and (with `seen`) not used before.
 */
export async function verifyPeerToken(token, jwks, expect) {
    const { typ, claims: raw } = await verifyJws(token, jwks);
    if (typ !== PEER_TYP)
        throw new SignatureError("not a peer token");
    const claims = raw;
    const now = expect.now ?? Math.floor(Date.now() / 1000);
    const skew = expect.skewSeconds ?? 30;
    if (claims.aud !== expect.nodeId)
        throw new SignatureError("issued for another node");
    if (expect.issuer && claims.iss !== expect.issuer)
        throw new SignatureError("issued by another platform");
    if (claims.exp + skew < now || claims.iat - skew > now)
        throw new SignatureError("expired");
    if (claims.exp - claims.iat > (expect.maxLifetimeSeconds ?? 600))
        throw new SignatureError("lives too long");
    if (expect.seen && !expect.seen.accept(claims.jti, claims.exp, now))
        throw new SignatureError("replayed");
    return claims;
}
/**
 * Verify a signature for this device and this body. Throws SignatureError with
 * a reason; returns the claims when every check passes.
 */
export async function verifyControlRequest(token, jwks, expect) {
    const { typ, claims: raw } = await verifyJws(token, jwks);
    if (typ === PEER_TYP)
        throw new SignatureError("a peer token is not a control request");
    const claims = raw;
    const now = expect.now ?? Math.floor(Date.now() / 1000);
    const skew = expect.skewSeconds ?? 30;
    if (claims.aud !== expect.deviceId)
        throw new SignatureError("signed for another device");
    if (expect.issuer && claims.iss !== expect.issuer)
        throw new SignatureError("signed by another platform");
    if (claims.exp + skew < now || claims.iat - skew > now)
        throw new SignatureError("expired");
    if (claims.body !== (await sha256B64url(expect.body)))
        throw new SignatureError("body does not match the signature");
    if (expect.seen && !expect.seen.accept(claims.jti, claims.exp, now))
        throw new SignatureError("replayed");
    return claims;
}
//# sourceMappingURL=controlSignature.js.map